import removeUndefined from '@lib/utils/removeUndefined';
import React from 'react';
import { TFormLayout, TFormSize, TFormVariant } from './Form.types';

export interface IFormSettingsContext {
  /**
   * Size of field.
   */
  size?: TFormSize;
  /**
   * Layout of form
   */
  layout?: TFormLayout;
  /**
   * Variant of form inputs.
   * on-light/on-dark backgrounds
   */
  variant?: TFormVariant;
  /**
   * Scroll to field when error.
   * You need to use ScrollView from Form component
   */
  scrollOnError?: boolean;
  /**
   * show feedback in form of icon
   */
  showFeedback?: boolean;
  /**
   * Indicate we are in form, so components adjust slightly
   */
  inForm?: boolean;
  disabled?: boolean;
  showLabel?: boolean;
}

export type TExtendedContext = IFormSettingsContext & {
  register?: (name: string, handler: () => void) => void;
  unregister?: (name: string) => void;
};

const formSettingsContext = React.createContext<TExtendedContext>({
  size: 'default',
  layout: 'vertical',
  variant: 'on-light',
  scrollOnError: true,
  showFeedback: true,
  inForm: false,
  disabled: false,
  showLabel: true,
});

export const FormSettingsProvider = formSettingsContext.Provider;
export const FormSettingsConsumer = formSettingsContext.Consumer;
export const useFormSettingsContext = <T extends Record<string, any>>(
  mergeProps?: T,
): T & TExtendedContext => {
  const original = React.useContext(formSettingsContext);
  const deps = mergeProps ? Object.values(mergeProps) : [];

  return React.useMemo(() => {
    return { ...original, ...removeUndefined(mergeProps || {}) } as T & IFormSettingsContext;
  }, [...deps, original]);
};
